import React, { useState } from "react";
import {
    selectConstellationFilters,
    selectFiltersVisibility,
    selectSearchInput,
    useCarbonDispatch,
    useCarbonSelector,
} from "../../store/carbonStore";
import EmptyPlaceholder from "../../common/components/placeholders/EmptyPlaceholder";
import { Button, UserfulSearchBar, UserfulTooltip } from "userful-chronos-common-ui/dist";
import { FiltersActions } from "../../store/filters/filtersSlice";
import ConstellationCard from "./constellation/constellationCard/ConstellationCard";
import CreateConstellation from "./constellation/CreateConstellation";
import { IConstellationInfo } from "../../model/CarbonExternalModels";
import { filterConstellations } from "../../store/filters/filtersManager";
import { GcmUserPermission } from "userful-chronos-app-common-js/dist/models/gcm-user-permission";
import { getGlobalStates } from "userful-chronos-app-common-js/dist/globalstates/globalStates";
import MultiselectConstellations from "./multiselect/MultiselectConstellations";
import SortConstellations from "./sort/SortConstellations";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMultiply } from "@fortawesome/pro-regular-svg-icons";
import ActionsConstellations from "./actions/ActionsConstellations";

interface IProps {
    constellations: { [id: string]: IConstellationInfo };
}

export interface ISortConstellations {
    element: string;
    sort: "ASC" | "DESC";
}

export default function ConstellationsTable(props: IProps) {
    const dispatch = useCarbonDispatch();

    const [selectedConstellations, setSelectedConstellations] = useState<{ [id: string]: IConstellationInfo }>({});
    const [sort, setSort] = useState<ISortConstellations>({ element: "name", sort: "ASC" });

    const permissions: GcmUserPermission = getGlobalStates().gcmUserPermission;

    // Visibility
    const filtersVisibility = useCarbonSelector(selectFiltersVisibility);

    // Filtering
    const constellationFilters = useCarbonSelector(selectConstellationFilters);
    const searchInput = useCarbonSelector(selectSearchInput);

    // Data
    const allConstellations: IConstellationInfo[] = [];
    for (const property in props.constellations) {
        allConstellations.push(props.constellations[property]);
    }
    const filteredConstellations = filterConstellations(
        constellationFilters,
        searchInput.constellations,
        allConstellations,
    );

    // Functions
    const onSearchBarChange = function (input: string) {
        dispatch(FiltersActions.setSearchInput({ ...searchInput, constellations: input }));
    };
    const onConstellationSelected = function (constellation: IConstellationInfo) {
        if (selectedConstellations[constellation.definition.id.value]) {
            const newSelectedWorkspaces = { ...selectedConstellations };
            delete newSelectedWorkspaces[constellation.definition.id.value];
            setSelectedConstellations(newSelectedWorkspaces);
        } else {
            setSelectedConstellations({
                ...selectedConstellations,
                [constellation.definition.id.value]: constellation,
            });
        }
    };

    const selectedLength: number = Object.keys(selectedConstellations).length;

    // Renders
    const insertBody = () => {
        if (allConstellations.length > 0) {
            if (filteredConstellations.length > 0) {
                return (
                    <>
                        {filteredConstellations
                            .sort((a, b) => {
                                if (sort.element === "name") {
                                    if (sort.sort === "ASC") return a.definition.name.localeCompare(b.definition.name);
                                    return b.definition.name.localeCompare(a.definition.name);
                                }
                            })
                            .map((c) => (
                                <ConstellationCard
                                    key={c.definition.id.value}
                                    constellation={c}
                                    onClick={() => {
                                        onConstellationSelected(c);
                                    }}
                                    selected={selectedConstellations[c.definition.id.value] ? true : false}
                                />
                            ))}
                    </>
                );
            } else {
                return (
                    <EmptyPlaceholder
                        img="./images/notFound.png"
                        title={"No Constellations Found"}
                        description={"Please refine your search/filter criteria and try again."}
                    />
                );
            }
        } else {
            return (
                <EmptyPlaceholder
                    img="./images/notFound.png"
                    title={"No Constellations Available"}
                    description={"There are no available constellations, click Create Constellation button."}
                    additionalComponents={<CreateConstellation type={"SECONDARY"} permissions={permissions} />}
                />
            );
        }
    };

    if (!permissions.isConstellationViewer) return null;

    return (
        <div className="gcm-contents-wrapper">
            <div className="gcm-contents-header-wrapper">
                <div className="gcm-contents-sub-header-wrapper gcm-contents-sub-header-priority-wrapper">
                    <MultiselectConstellations
                        constellations={props.constellations}
                        selectedConstellations={selectedConstellations}
                        onSelectedconstellationsChange={(constellations: { [id: string]: IConstellationInfo }) => {
                            setSelectedConstellations(constellations);
                        }}
                    />
                    <UserfulSearchBar forceValue={searchInput.constellations} onChange={onSearchBarChange} />
                    <SortConstellations onSortChange={(sort) => setSort(sort)} sort={sort} />
                    {selectedLength > 0 && (
                        <>
                            <ActionsConstellations
                                selectedConstellations={selectedConstellations}
                                onSelectedconstellationsChange={(constellations: {
                                    [id: string]: IConstellationInfo;
                                }) => {
                                    setSelectedConstellations(constellations);
                                }}
                            />
                            <UserfulTooltip title={"Deselect"} delay={1000}>
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        setSelectedConstellations({});
                                    }}
                                >
                                    <FontAwesomeIcon icon={faMultiply} />
                                    Selected ({selectedLength})
                                </Button>
                            </UserfulTooltip>
                        </>
                    )}
                </div>
                <div className="gcm-contents-sub-header-wrapper">
                    <CreateConstellation type={"PRIMARY"} permissions={permissions} />
                </div>
            </div>
            <div className="gcm-contents-body-wrapper">{insertBody()}</div>
        </div>
    );
}
